import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Header, Icon, Label, Segment, Table } from 'semantic-ui-react';
import S from '../core.js';
import { statusOf, stageLabel } from '../approvals.js';
import { periodOf } from '../periods.js';

const keyOf = (r) => `${r.settlement_report_id}/${r.report_year}/${r.as_at_date}`;

/* The Reports menu for one account manager: a checkbox per report and a "Generate all to PDF" button.
 * Only reports whose Annual period has both Finance and AM approval can be ticked; the rest are listed with their approval stage. */
export default function ManagerReports({ manager, reports, approvals, busy, onOpen, onGenerate }) {
  const [skipped, setSkipped] = useState(() => new Set());           // keys that are unticked (so new reports default to ticked)
  const all = useRef(null);
  const stages = useMemo(() => new Map(reports.map((r) => [keyOf(r), statusOf(approvals, r, periodOf({ mode: 'annual', year: r.report_year })).stage])), [reports, approvals]);
  const eligible = useMemo(() => reports.filter((r) => stages.get(keyOf(r)) === 'complete'), [reports, stages]);
  const chosen = useMemo(() => eligible.filter((r) => !skipped.has(keyOf(r))), [eligible, skipped]);
  const none = chosen.length === 0, every = eligible.length > 0 && chosen.length === eligible.length;

  useEffect(() => { if (all.current) all.current.indeterminate = !none && !every; }, [none, every]);

  const toggle = (r) => setSkipped((s) => { const n = new Set(s); const k = keyOf(r); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const toggleAll = () => setSkipped(every ? new Set(eligible.map(keyOf)) : new Set());

  return (
    <section aria-labelledby="mgr-heading">
      <Header as="h2" id="mgr-heading" className="titlebar">
        <Icon name="user circle outline" />
        <Header.Content>
          {manager} – Reports
          <Header.Subheader>{reports.length} report{reports.length === 1 ? '' : 's'}</Header.Subheader>
        </Header.Content>
      </Header>

      {reports.length === 0 ? (
        <Segment placeholder><Header icon><Icon name="folder open outline" />{manager} has no reports yet.</Header></Segment>
      ) : (
        <>
          <div className="toolbar">
            <Button primary type="button" icon="file pdf outline" labelPosition="left" loading={busy === 'bulk'} disabled={none || !!busy}
              content="Generate all to PDF" onClick={() => onGenerate(chosen)} />
            <span className="hint" role="status">{chosen.length} of {eligible.length} approved report{eligible.length === 1 ? '' : 's'} selected</span>
          </div>
          <Table celled compact unstackable className="mgrtable">
            <caption className="visually-hidden">Reports for {manager}</caption>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell collapsing scope="col">
                  <input ref={all} type="checkbox" checked={every} disabled={eligible.length === 0} onChange={toggleAll} aria-label="Select all approved reports" />
                </Table.HeaderCell>
                <Table.HeaderCell scope="col">Report</Table.HeaderCell>
                <Table.HeaderCell scope="col">Client</Table.HeaderCell>
                <Table.HeaderCell scope="col">Year</Table.HeaderCell>
                <Table.HeaderCell scope="col">As at</Table.HeaderCell>
                <Table.HeaderCell scope="col">Annual approval</Table.HeaderCell>
                <Table.HeaderCell collapsing scope="col"><span className="visually-hidden">Open</span></Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {reports.map((r) => (
                <Table.Row key={keyOf(r)}>
                  <Table.Cell collapsing>
                    <input type="checkbox" checked={stages.get(keyOf(r)) === 'complete' && !skipped.has(keyOf(r))} disabled={stages.get(keyOf(r)) !== 'complete'}
                      onChange={() => toggle(r)} aria-label={`Include ${r.settlement_report_id} ${r.report_year} in the PDF`} />
                  </Table.Cell>
                  <Table.Cell><strong>{r.settlement_report_id}</strong></Table.Cell>
                  <Table.Cell>{r.client_name || '–'}</Table.Cell>
                  <Table.Cell>{r.report_year}</Table.Cell>
                  <Table.Cell><time dateTime={r.as_at_date}>{S.dmy(r.as_at_date)}</time></Table.Cell>
                  <Table.Cell><Label size="small" color={stages.get(keyOf(r)) === 'complete' ? 'green' : stages.get(keyOf(r)) === 'finance' ? 'yellow' : undefined}>{stageLabel(stages.get(keyOf(r)))}</Label></Table.Cell>
                  <Table.Cell collapsing>
                    <Button type="button" basic compact size="small" aria-label={`Open ${r.settlement_report_id} ${r.report_year}`} onClick={() => onOpen(r)}>Open</Button>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </>
      )}
    </section>
  );
}
